import * as React from 'react';
import { Icon } from '../icons.jsx';
import { Badge, Placeholder, ProviderPhoto } from '../ui.jsx';
import { AGE_GROUPS } from '../data';
import { api } from '../api';
import { useApi, Skeleton, ErrorState } from '../hooks';
import { formatAvailabilityFreshness } from '../freshness';

// Provider Profile page

const AvailabilityCell = ({ group, info }) => {
  const open = info.spots > 0;
  const notOffered = info.note === 'Not offered';
  return (
    <div style={{
      background: open ? 'var(--brand-tint)' : notOffered ? 'var(--surface)' : 'var(--wait-soft)',
      border: `1px solid ${open ? 'rgba(26,102,213,0.22)' : notOffered ? 'var(--line)' : 'rgba(176,122,28,0.25)'}`,
      borderRadius: 'var(--r-md)',
      padding: 18,
      opacity: notOffered ? 0.55 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>{group}</span>
        {open
          ? <Badge tone="open" icon="live">Open</Badge>
          : notOffered
          ? <span style={{ fontSize: 11, color: 'var(--ink-4)', fontWeight: 600 }}>N/A</span>
          : <Badge tone="wait">Waitlist</Badge>}
      </div>
      <div style={{
        fontSize: 32, fontWeight: 700, color: open ? 'var(--brand-ink)' : notOffered ? 'var(--ink-4)' : 'var(--wait)',
        letterSpacing: '-0.02em', lineHeight: 1,
      }}>
        {open ? info.spots : notOffered ? 'N/A' : info.note.includes('Waitlist') ? info.note.match(/\d+/)?.[0] || 0 : 0}
        {open && <span style={{ fontSize: 14, color: 'var(--ink-3)', fontWeight: 500, marginLeft: 6 }}>{info.spots === 1 ? 'spot' : 'spots'}</span>}
        {!open && !notOffered && <span style={{ fontSize: 14, color: 'var(--ink-3)', fontWeight: 500, marginLeft: 6 }}>ahead</span>}
      </div>
      {!notOffered && (
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8 }}>
          {info.note}
        </div>
      )}
    </div>
  );
};

const ActionTile = ({ icon, title, subtitle, onClick, primary }) => {
  const [hov, setHov] = React.useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: primary ? 'var(--brand)' : 'var(--surface)',
        color: primary ? '#fff' : 'var(--ink)',
        border: primary ? '1px solid var(--brand)' : '1px solid var(--line-2)',
        borderRadius: 'var(--r-md)',
        padding: '18px 18px',
        textAlign: 'left', cursor: 'pointer',
        boxShadow: hov ? 'var(--shadow-md)' : 'var(--shadow-sm)',
        transform: hov ? 'translateY(-2px)' : 'none',
        transition: 'all .18s ease',
        display: 'flex', alignItems: 'center', gap: 14,
        fontFamily: 'inherit',
      }}
    >
      <span style={{
        width: 44, height: 44, borderRadius: 12,
        background: primary ? 'rgba(255,255,255,0.16)' : 'var(--brand-tint)',
        color: primary ? '#fff' : 'var(--brand)',
        display: 'grid', placeItems: 'center', flexShrink: 0,
      }}>
        <Icon name={icon} size={20}/>
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>{title}</div>
        <div style={{ fontSize: 12, opacity: primary ? 0.75 : 0.6 }}>{subtitle}</div>
      </div>
      <Icon name="chev-right" size={16} style={{ opacity: 0.5 }}/>
    </button>
  );
};

export const ProfileScreen = ({ go, providerId, onBook, onWaitlist = () => {} }) => {
  const { data: p, loading, error, reload } = useApi(
    () => api.providers.get(providerId),
    [providerId],
  );
  const {
    data: posts,
    loading: postsLoading,
    error: postsError,
    reload: reloadPosts,
  } = useApi(() => api.posts.list(providerId), [providerId]);
  const { data: notices } = useApi(() => api.notices.list(providerId), [providerId]);

  if (loading) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <div style={{ padding: 24, paddingBottom: 0 }}>
          <div className="container">
            <Skeleton height={16} width={120}/>
          </div>
        </div>
        <section style={{ padding: '14px 24px 0' }}>
          <div className="container" style={{ padding: 0 }}>
            <Skeleton height={320} radius={24}/>
            <div style={{ padding: '28px 0', display: 'grid', gap: 12 }}>
              <Skeleton height={32} width="60%"/>
              <Skeleton height={16} width="40%"/>
              <Skeleton height={16} width="80%"/>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (error || !p) {
    return (
      <div className="container" style={{ padding: '48px 24px' }}>
        <ErrorState error={error || new Error('Provider not found')} onRetry={reload}/>
      </div>
    );
  }

  const isOpen = p.status === 'open';

  return (
    <div style={{ paddingBottom: 80 }}>
      {/* Hero photo (full width) */}
      <div style={{ padding: 24, paddingBottom: 0 }}>
        <div className="container">
          <button
            onClick={() => go('results')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--ink-3)', fontSize: 13, fontWeight: 600,
              padding: '6px 0', marginBottom: 14,
            }}
          >
            <Icon name="chev-left" size={14}/>Back to results
          </button>
        </div>
      </div>

      <section style={{ padding: '0 24px' }}>
        <div className="container" style={{ padding: 0 }}>
          <div style={{ position: 'relative' }}>
            {p.image
              ? <ProviderPhoto className="profile-hero" src={p.image} alt={`${p.name} childcare space`} radius={24} height={320} label={p.neighborhood} loading="eager"/>
              : <Placeholder className="profile-hero" tone={p.tone} radius={24} height={320} label={p.neighborhood}/>}
            <div style={{
              position: 'absolute', left: 18, bottom: 18,
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(255,255,255,0.82)', border: '1px solid rgba(255,255,255,0.58)',
              borderRadius: 999, padding: '8px 12px', boxShadow: 'var(--shadow-sm)',
              color: 'var(--ink-2)', fontSize: 13, fontWeight: 800,
              backdropFilter: 'blur(10px)',
            }}>
              <span className="live-dot" aria-hidden="true" /><span className="sr-only">Live availability.</span> Verified availability · updated {formatAvailabilityFreshness(p.availabilityUpdatedAt)}
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: '36px 0 0' }}>
        <div className="container">
          {notices?.length > 0 && (
            <div className="provider-notice" role="status">
              <span><Icon name="bell" size={18}/></span>
              <div>
                <strong>Special hours for {notices[0].dateLabel}</strong>
                <p>{notices[0].message}</p>
              </div>
            </div>
          )}
          <div className="profile-grid" style={{
            display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px',
            gap: 48,
          }}>
            {/* MAIN COLUMN ----------------------------- */}
            <div>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                {isOpen
                  ? <Badge tone="open" icon="live">{p.spots} open spots</Badge>
                  : <Badge tone="wait">Waitlist Only</Badge>}
                {p.subsidy && <Badge tone="brand" icon="shield">Subsidy accepted</Badge>}
                <Badge tone="neutral" icon="shield">Illustrative provider profile</Badge>
              </div>

              <h1 style={{
                fontSize: 'clamp(28px, 3.4vw, 40px)', fontWeight: 700,
                color: 'var(--ink)', letterSpacing: '-0.025em', lineHeight: 1.08,
                marginBottom: 10,
              }}>
                {p.name}
              </h1>

              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, color: 'var(--ink-2)',
                fontSize: 15, marginBottom: 28,
              }}>
                <Icon name="pin" size={15} style={{ color: 'var(--ink-3)' }}/>
                <span>{p.address}</span>
                <span style={{ color: 'var(--ink-4)' }}>·</span>
                <span>{p.distance}</span>
              </div>

              {/* Description */}
              <p style={{
                fontSize: 16, lineHeight: 1.6, color: 'var(--ink-2)',
                maxWidth: 680, marginBottom: 36,
              }}>
                {p.description}
              </p>

              {/* Quick facts row */}
              <div className="quick-facts" style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1,
                background: 'var(--line)', border: '1px solid var(--line)',
                borderRadius: 'var(--r-md)', overflow: 'hidden', marginBottom: 40,
              }}>
                {[
                  { label: 'Hours',     value: p.hours },
                  { label: 'Languages', value: p.languages },
                  { label: 'Subsidy',   value: p.subsidy ? 'CCIS · ELRC' : 'Not currently' },
                ].map(f => (
                  <div key={f.label} style={{ background: 'var(--surface)', padding: '16px 18px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
                      {f.label}
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 500 }}>{f.value}</div>
                  </div>
                ))}
              </div>

              {/* Availability ============================= */}
              <h2 style={{
                fontSize: 22, fontWeight: 700, color: 'var(--ink)',
                letterSpacing: '-0.02em', marginBottom: 4,
              }}>
                Availability by age group
              </h2>
              <p style={{ color: 'var(--ink-3)', fontSize: 14, marginBottom: 20 }}>
                Live counts. Updated <strong style={{ color: 'var(--ink-2)', fontWeight: 600 }}>{formatAvailabilityFreshness(p.availabilityUpdatedAt)}</strong>.
              </p>

              <div className="availability-grid" style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14,
                marginBottom: 44,
              }}>
                {AGE_GROUPS.map(g => (
                  <AvailabilityCell key={g} group={g} info={p.availability[g]}/>
                ))}
              </div>

              {/* Photo gallery */}
              <h2 style={{
                fontSize: 22, fontWeight: 700, color: 'var(--ink)',
                letterSpacing: '-0.02em', marginBottom: 14,
              }}>
                Inside the center
              </h2>
              <div className="gallery-grid" style={{
                display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 10,
              }}>
                {p.gallery?.length
                  ? p.gallery.map(item => (
                    <ProviderPhoto
                      key={item.label}
                      src={item.src}
                      alt={`${p.name} ${item.label.toLowerCase()}`}
                      radius={12}
                      height={130}
                      label={item.label}
                    />
                  ))
                  : ['classroom', 'outdoor garden', 'art corner', 'naptime'].map((l, i) => (
                    <Placeholder
                      key={l}
                      tone={['A','B','C','D'][i]}
                      radius={12}
                      height={130}
                      label={p.neighborhood}
                    />
                  ))}
              </div>

              {(postsLoading || postsError || posts?.length > 0) && (
                <section className="provider-updates" aria-labelledby="provider-updates-title">
                  <div className="provider-updates-heading">
                    <div>
                      <span>From the provider</span>
                      <h2 id="provider-updates-title">Latest from the center</h2>
                    </div>
                    <Badge tone="neutral">Provider supplied</Badge>
                  </div>

                  {postsLoading && <Skeleton height={360} radius={18}/>}
                  {postsError && (
                    <div className="provider-updates-error" role="alert">
                      We couldn’t load this center’s latest posts.
                      <button type="button" onClick={reloadPosts}>Try again</button>
                    </div>
                  )}
                  {!postsLoading && !postsError && posts?.map((post) => (
                    <article className="provider-post" key={post.id}>
                      <ProviderPhoto
                        src={post.image}
                        alt={post.altText}
                        radius={16}
                        height={300}
                      />
                      <div>
                        <time dateTime={post.publishedAt}>
                          {new Intl.DateTimeFormat('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          }).format(new Date(post.publishedAt))}
                        </time>
                        <p>{post.caption}</p>
                      </div>
                    </article>
                  ))}
                </section>
              )}
            </div>

            {/* SIDEBAR (actions) ----------------------- */}
            <aside style={{ position: 'relative' }}>
              <div className="profile-sticky" style={{
                position: 'sticky', top: 88,
                background: 'var(--surface)',
                border: '1px solid var(--line)',
                borderRadius: 'var(--r-lg)',
                padding: 18,
                boxShadow: 'var(--shadow-sm)',
              }}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>
                    How would you like to visit?
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                    No account needed. Free for families.
                  </div>
                </div>

                <div style={{ display: 'grid', gap: 10 }}>
                  <ActionTile
                    primary
                    icon="calendar"
                    title="Visit in Person"
                    subtitle="Pick a time at the center"
                    onClick={() => onBook(p.id, 'in-person')}
                  />
                  <ActionTile
                    icon="video"
                    title="Virtual Tour"
                    subtitle="Live walkthrough on video"
                    onClick={() => onBook(p.id, 'virtual')}
                  />
                  <ActionTile
                    primary={!isOpen}
                    icon="inbox"
                    title="Join Waitlist"
                    subtitle="The center will contact you when a spot opens"
                    onClick={() => onWaitlist(p.id)}
                  />
                  <ActionTile
                    icon="phone"
                    title="Call Center"
                    subtitle="(215) 555-0184"
                    onClick={() => {}}
                  />
                </div>

                <div style={{
                  marginTop: 18, padding: '14px 14px',
                  background: 'var(--brand-tint)', borderRadius: 12,
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                }}>
                  <Icon name="shield" size={16} style={{ color: 'var(--brand)', marginTop: 1, flexShrink: 0 }}/>
                  <div style={{ fontSize: 12, color: 'var(--brand-ink)', lineHeight: 1.5 }}>
                    <strong>Official licensing information would appear here.</strong>{' '}
                    Production profiles would link to Pennsylvania&rsquo;s provider record.
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};
